import { useToast } from '@contexts/Toast';
import { Axios } from '@helpers/ApiService';
import { errorMessage } from '@helpers/Constants';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { Subscription } from '@helpers/Models';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export const Cancelled = () => {
  const navigate = useNavigate();
  const { showToastMessage } = useToast();

  const [subscriptionDetails, setSubscriptionDetails] =
    useState<Subscription | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    fetchSubscriptionDetails();
  }, []);

  /**
   * Fetches the current subscription details from the backend.
   *
   * @async
   * @function fetchSubscriptionDetails
   * @returns {Promise<void>}
   *
   * @remarks
   * - Retrieves subscription data using the API endpoint.
   * - Updates the local state with the fetched subscription details.
   * - Stores the latest subscription status in `localStorage` for global access.
   * - Displays an error toast message if the request fails.
   */
  const fetchSubscriptionDetails = async () => {
    try {
      setLoading(true);
      const response = await Axios.get(
        ApiEndpoints.FETCH_BUTCHER_SUBSCRIPTION_DETAILS,
      );
      setSubscriptionDetails(response.data.data.subscription);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`p-2 ${loading ? 'opacity-50' : 'opacity-100'}`}>
      {/* Cancelled banner */}
      <div className="px-6 py-5 bg-[#FEF2F2]">
        {/* Top row */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            {/* Cancelled icon */}
            <svg
              width="19"
              height="19"
              viewBox="0 0 19 19"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12.6093 6.91259L10.2639 9.25714L12.6093 11.6017C12.6755 11.6678 12.7279 11.7464 12.7637 11.8328C12.7996 11.9193 12.818 12.0119 12.818 12.1055C12.818 12.1991 12.7996 12.2917 12.7637 12.3781C12.7279 12.4646 12.6755 12.5431 12.6093 12.6093C12.5431 12.6755 12.4646 12.7279 12.3782 12.7637C12.2917 12.7995 12.1991 12.818 12.1055 12.818C12.0119 12.818 11.9193 12.7995 11.8328 12.7637C11.7464 12.7279 11.6679 12.6755 11.6017 12.6093L9.25714 10.2639L6.9126 12.6093C6.84644 12.6755 6.76789 12.7279 6.68145 12.7637C6.59501 12.7995 6.50236 12.818 6.40879 12.818C6.31523 12.818 6.22258 12.7995 6.13614 12.7637C6.0497 12.7279 5.97115 12.6755 5.90499 12.6093C5.83883 12.5431 5.78635 12.4646 5.75054 12.3781C5.71474 12.2917 5.69631 12.1991 5.69631 12.1055C5.69631 12.0119 5.71474 11.9193 5.75054 11.8328C5.78635 11.7464 5.83883 11.6678 5.90499 11.6017L8.25043 9.25714L5.90499 6.91259C5.77138 6.77898 5.69631 6.59775 5.69631 6.40879C5.69631 6.21983 5.77138 6.0386 5.90499 5.90499C6.03861 5.77137 6.21983 5.69631 6.40879 5.69631C6.59776 5.69631 6.77898 5.77137 6.9126 5.90499L9.25714 8.25043L11.6017 5.90499C11.6679 5.83883 11.7464 5.78635 11.8328 5.75054C11.9193 5.71473 12.0119 5.69631 12.1055 5.69631C12.1991 5.69631 12.2917 5.71473 12.3782 5.75054C12.4646 5.78635 12.5431 5.83883 12.6093 5.90499C12.6755 5.97115 12.7279 6.04969 12.7637 6.13613C12.7996 6.22258 12.818 6.31523 12.818 6.40879C12.818 6.50235 12.7996 6.595 12.7637 6.68145C12.7279 6.76789 12.6755 6.84643 12.6093 6.91259ZM18.5143 9.25714C18.5143 11.088 17.9714 12.8778 16.9542 14.4001C15.937 15.9225 14.4912 17.109 12.7997 17.8096C11.1082 18.5103 9.24688 18.6936 7.45117 18.3364C5.65546 17.9792 4.00599 17.0976 2.71136 15.8029C1.41672 14.5083 0.535066 12.8588 0.177878 11.0631C-0.179311 9.26741 0.00401098 7.40611 0.704662 5.71459C1.40531 4.02307 2.59182 2.5773 4.11415 1.56011C5.63648 0.542922 7.42626 0 9.25714 0C11.7115 0.00259184 14.0646 0.978727 15.8001 2.71422C17.5356 4.44971 18.5117 6.80279 18.5143 9.25714ZM17.0901 9.25714C17.0901 7.70793 16.6307 6.1935 15.77 4.90538C14.9093 3.61725 13.686 2.61328 12.2547 2.02042C10.8234 1.42757 9.24846 1.27245 7.72901 1.57468C6.20956 1.87692 4.81386 2.62294 3.7184 3.7184C2.62294 4.81386 1.87692 6.20956 1.57469 7.72901C1.27245 9.24845 1.42757 10.8234 2.02043 12.2547C2.61329 13.686 3.61726 14.9093 4.90538 15.77C6.19351 16.6307 7.70793 17.0901 9.25714 17.0901C11.3339 17.0878 13.3248 16.2617 14.7933 14.7933C16.2617 13.3248 17.0878 11.3338 17.0901 9.25714Z"
                fill="#991B1B"
              />
            </svg>
            <span className="text-[#434343] text-[24px] font-bold">
              Subscription Cancelled
            </span>
          </div>

          <button
            type="button"
            onClick={() => navigate(FrontendRoutes.SUBSCRIPTION_REACTIVATE)}
            className="flex items-center gap-1.5 text-primary font-bold text-[16px]"
          >
            Reactivate Subscription
            <svg
              width="18"
              height="15"
              viewBox="0 0 18 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M-1.90735e-05 7.22173C-1.90735e-05 6.94822 0.108631 6.68592 0.302027 6.49252C0.495424 6.29913 0.757727 6.19048 1.03123 6.19048L13.664 6.19048L9.23654 1.76384C9.04281 1.5701 8.93397 1.30735 8.93397 1.03337C8.93397 0.759389 9.04281 0.496632 9.23654 0.302898C9.43028 0.109166 9.69303 0.000328064 9.96701 0.000328064C10.241 0.000328064 10.5037 0.109166 10.6975 0.302898L16.885 6.4904C16.9811 6.58621 17.0574 6.70005 17.1095 6.8254C17.1615 6.95075 17.1883 7.08514 17.1883 7.22087C17.1883 7.35659 17.1615 7.49099 17.1095 7.61634C17.0574 7.74169 16.9811 7.85553 16.885 7.95134L10.6975 14.1388C10.6016 14.2348 10.4877 14.3109 10.3623 14.3628C10.237 14.4147 10.1027 14.4414 9.96701 14.4414C9.83135 14.4414 9.69702 14.4147 9.57168 14.3628C9.44635 14.3109 9.33247 14.2348 9.23654 14.1388C9.14062 14.0429 9.06452 13.929 9.01261 13.8037C8.96069 13.6784 8.93397 13.544 8.93397 13.4084C8.93397 13.2727 8.96069 13.1384 9.01261 13.013C9.06452 12.8877 9.14062 12.7738 9.23654 12.6779L13.664 8.25298L1.03123 8.25298C0.757727 8.25298 0.495424 8.14433 0.302027 7.95093C0.108631 7.75753 -1.90735e-05 7.49523 -1.90735e-05 7.22173Z"
                fill="#495057"
              />
            </svg>
          </button>
        </div>

        {/* Description */}
        <p className="text-[#434343F2] text-sm leading-relaxed font-medium pl-8">
          Your subscription has been cancelled. You can reactivate it anytime
          within one year.
        </p>
      </div>
      <div className="border border-[#E8E8E8] p-3 rounded-md mt-5">
        <div className="flex flex-row justify-between">
          <div className="flex-1">
            <span className="text-2xl text-[#292D32] font-bold">
              {subscriptionDetails?.subscriptionPlan}
            </span>
            <div className="inline-block mb-1 ml-2">
              <div className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full bg-[#292D321A] text-[#292D32] w-fit">
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0 5C0 2.23858 2.23858 0 5 0C7.76142 0 10 2.23858 10 5C10 7.76142 7.76142 10 5 10C2.23858 10 0 7.76142 0 5Z"
                    fill="#991B1B"
                  />
                </svg>
                <span className="uppercase text-[#292D32] text-[12px]">
                  CANCELLED
                </span>
              </div>
            </div>
          </div>
          <div className="flex">
            <button
              type="button"
              onClick={() => navigate(FrontendRoutes.SUBSCRIPTION_REACTIVATE)}
              className="flex flex-row gap-3 font-bold text-[16px]"
            >
              <span className="text-primary">Reactivate Subscription</span>
              <span className="mt-2">
                <svg
                  width="18"
                  height="15"
                  viewBox="0 0 18 15"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M-1.90735e-05 7.22173C-1.90735e-05 6.94822 0.108631 6.68592 0.302027 6.49252C0.495424 6.29913 0.757727 6.19048 1.03123 6.19048L13.664 6.19048L9.23654 1.76384C9.04281 1.5701 8.93397 1.30735 8.93397 1.03337C8.93397 0.759389 9.04281 0.496632 9.23654 0.302898C9.43028 0.109166 9.69303 0.000328064 9.96701 0.000328064C10.241 0.000328064 10.5037 0.109166 10.6975 0.302898L16.885 6.4904C16.9811 6.58621 17.0574 6.70005 17.1095 6.8254C17.1615 6.95075 17.1883 7.08514 17.1883 7.22087C17.1883 7.35659 17.1615 7.49099 17.1095 7.61634C17.0574 7.74169 16.9811 7.85553 16.885 7.95134L10.6975 14.1388C10.6016 14.2348 10.4877 14.3109 10.3623 14.3628C10.237 14.4147 10.1027 14.4414 9.96701 14.4414C9.83135 14.4414 9.69702 14.4147 9.57168 14.3628C9.44635 14.3109 9.33247 14.2348 9.23654 14.1388C9.14062 14.0429 9.06452 13.929 9.01261 13.8037C8.96069 13.6784 8.93397 13.544 8.93397 13.4084C8.93397 13.2727 8.96069 13.1384 9.01261 13.013C9.06452 12.8877 9.14062 12.7738 9.23654 12.6779L13.664 8.25298L1.03123 8.25298C0.757727 8.25298 0.495424 8.14433 0.302027 7.95093C0.108631 7.75753 -1.90735e-05 7.49523 -1.90735e-05 7.22173Z"
                    fill="#495057"
                  />
                </svg>
              </span>
            </button>
          </div>
        </div>
        <div className="flex flex-col">
          <div className="mt-1">
            <span className="font-bold text-2xl">$</span>
            <span className="text-4xl font-bold">1.99 </span>
            <span className="mb-3 text-[#292D32] font-medium"> /Animal</span>
          </div>
        </div>
      </div>
    </div>
  );
};
